import React, { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { UserSearchInput } from './TimeLog';
import { PriorityDropdown } from '../shared/fields/priority-icon';
import { ComplexityDots } from '../shared/fields/complexity-dots';
import TaskFileUpload from './TaskFileUpload';

// field_priority / field_complexity are OPTIONAL on creation: a ticket starts
// with priority "Non définie" and complexity unset (0 = empty in the widgets).
// The save paths OMIT these keys when 0 so Drupal stores them empty.
/**
 * Texte pré-rédigé → HTML, pour semer l'éditeur riche.
 *
 * TipTap attend du HTML : lui donner du texte brut colle tous les paragraphes
 * les uns aux autres. La conversion reste volontairement minimale — paragraphes,
 * retours à la ligne, listes à puces — parce que c'est tout ce que contient une
 * description proposée par l'assistant, à qui son gabarit demande des phrases.
 *
 * Tout est ÉCHAPPÉ puis reconstruit : ce texte ne vient pas d'un rédacteur
 * d'Orbit, et du HTML qu'il contiendrait n'a rien à faire dans le champ.
 */
const echapper = (s) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

function texteVersHtml(texte) {
    return String(texte)
        .replace(/\r\n/g, '\n')
        .split(/\n{2,}/)
        .map((bloc) => {
            const lignes = bloc.split('\n').map((l) => l.trim()).filter(Boolean);
            if (lignes.length === 0) return '';
            if (lignes.every((l) => /^[-*]\s+/.test(l))) {
                return `<ul>${lignes.map((l) => `<li>${echapper(l.replace(/^[-*]\s+/, ''))}</li>`).join('')}</ul>`;
            }
            return `<p>${lignes.map(echapper).join('<br>')}</p>`;
        })
        .filter(Boolean)
        .join('');
}

const DEFAULT_PRIORITY = 0;   // Non définie
const DEFAULT_COMPLEXITY = 0; // vide

const NewTaskModal = ({ isOpen, onClose, onSave, inline = false, taskType = '', isSubTask = false, users = [], returnTypeOptions = [], evolutionTypeOptions = [], tagOptions = [], typeOptions = [], enabledTaskTypes = {}, initialDescription = '' }) => {
  // taskType is the tid (from KanbanHeader) — use directly, no label resolution.
  const [title, setTitle] = useState('');
  const [type, setType] = useState('');
  // field_priority / field_complexity are OPTIONAL on creation; default 0
  // (Non définie / vide). The save paths omit them when < 1.
  const [priority, setPriority] = useState(DEFAULT_PRIORITY);
  const [complexity, setComplexity] = useState(DEFAULT_COMPLEXITY);
  const [tags, setTags] = useState([]);
  const [returnType, setReturnType] = useState('');
  const [evolutionType, setEvolutionType] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  // Échéance (field_due_date, datetime date-only) : la valeur d'un <input type="date">
  // est déjà au format YYYY-MM-DD attendu par Drupal ; vide = pas d'échéance.
  const [dueDate, setDueDate] = useState('');
  const [createAnother, setCreateAnother] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  // Attached files (deferred upload): TaskFileUpload uploads each pick immediately
  // and pushes { fid, url, filename, filemime, deleteUrl } here; the fids are sent
  // as attachedFiles on save, or deleted on cancel (orphan temporary files).
  const [files, setFiles] = useState([]);

  // Cancel/close WITHOUT creating → best-effort delete the uploaded temp files.
  const handleClose = () => {
    files.forEach((f) => { if (f.deleteUrl) fetch(f.deleteUrl, { method: 'POST', credentials: 'include' }).catch(() => {}); });
    setFiles([]);
    onClose();
  };

  // Rich-text editor for the description (TipTap), bound to local state.
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Description détaillée…' }),
    ],
    content: '',
    onUpdate: ({ editor: ed }) => setDescription(ed.getHTML()),
    editorProps: { attributes: { class: 'prose prose-sm max-w-none focus:outline-none min-h-[120px] px-3 py-2.5' } },
  });

  // Description pré-rédigée (proposition de l'assistant du builder, transmise
  // par le formulaire de signalement). Semée UNE FOIS par ouverture, dans l'état
  // ET dans l'éditeur : c'est exactement le chemin d'une saisie humaine, donc
  // rien ne change de ce qui finit en base.
  //
  // L'éditeur n'existe pas au premier rendu (`useEditor` rend null) : d'où la
  // dépendance à `editor` plutôt qu'un semis au montage.
  const semee = useRef('');
  useEffect(() => {
    const texte = String(initialDescription || '').trim();
    if (!texte || !editor) return;
    if (!isOpen && !inline) return;
    if (semee.current === texte) return;
    semee.current = texte;
    const html = texteVersHtml(texte);
    setDescription(html);
    editor.commands.setContent(html);
  }, [initialDescription, editor, isOpen, inline]);

  const demandeEvolutionTid = returnTypeOptions.find(o => o.label === "Demande d'évolution")?.value || '';
  const isDemandeEvolution = demandeEvolutionTid && String(returnType) === String(demandeEvolutionTid);

  // type is tid; get label for display and conditional checks (e.g. Evolution-specific evolutionType field)
  const typeLabel = typeOptions.find(o => String(o.value) === String(type))?.label || '';

  // Types proposés dans l'en-tête : EXACTEMENT ceux du bouton « + Nouveau »
  // (types de TÊTE autorisés pour le projet, déjà filtrés par droits côté
  // KanbanHeader et transmis via enabledTaskTypes). Les modales de sous-tâche
  // passent un enabledTaskTypes vide + un typeOptions déjà restreint → aucune
  // restriction supplémentaire ici.
  const noTypeRestriction = !enabledTaskTypes || Object.keys(enabledTaskTypes).length === 0;
  const visibleTypeOptions = typeOptions.filter((opt) => {
    if (noTypeRestriction) return true;
    // Garde le type courant pour qu'un <select> contrôlé ne devienne jamais vide.
    if (String(opt.value) === String(type)) return true;
    // Un sous-type (parentId > 0) n'est pas un type de création de tâche-mère.
    if (Number(opt.data?.parentId) > 0) return false;
    return enabledTaskTypes[opt.label] === true;
  });

  // « Type d'évolution » : affiché à DROITE du type, dans l'en-tête.
  const showEvolutionType = typeLabel === 'Evolution' || typeLabel === 'Évolution' || isDemandeEvolution;

  const findTagLabel = (tid) => {
    const opt = tagOptions.find(o => String(o.value) === String(tid));
    return opt ? opt.label : tid;
  };
  const findTagTid = (name) => {
    const opt = tagOptions.find(o => o.label === name);
    return opt ? opt.value : name;
  };
  const availableTagNames = tagOptions.map(o => o.label).filter(Boolean);
  
  // Styles
  const labelClass = "block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide ml-0.5";
  const inputBaseClass = "input";
  const selectBaseClass = "select";

  // Reset when opening — taskType is already the tid
  useEffect(() => {
    if (isOpen) {
        setTitle('');
        setType(String(taskType || ''));
        setPriority(DEFAULT_PRIORITY);
        setComplexity(DEFAULT_COMPLEXITY);
        setTags([]);
        setReturnType('');
        setEvolutionType('');
        setDescription('');
        editor?.commands.setContent('');
        setAssignedTo('');
        setDueDate('');
        setCreateAnother(false);
        setFiles([]);
    }
  }, [isOpen, taskType, isSubTask, editor]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title) return;  // "Type de retour" is now optional + hidden.
    // The TYPE is structural: without it the task has no workflow, hence no
    // initial state (orbit_task_node_presave has nothing to derive one from),
    // and it shows on no board. Same guard as the title. Callers that impose a
    // type (the Kanban) never hit it.
    if (!type) return;

    onSave({
        title,
        type: type,
        // Optional: pass the raw value (0 = unset). The save paths OMIT priority /
        // complexity when unset so Drupal stores them empty (sending 0 would
        // violate the field min:1 → 422).
        rating: priority,
        complexity: complexity,
        tags,
        assignee: assignedTo,
        // Échéance optionnelle : null quand aucune date n'est saisie.
        dueDate: dueDate || null,
        // Return type is hidden + optional now: include it only when actually set.
        ...(returnType ? { returnType } : {}),
        evolutionType: showEvolutionType ? evolutionType : null,
        description,
        // Deferred attachments: fids of the temporary files uploaded in the modal.
        attachedFiles: files.map(f => f.fid).filter(Boolean),
        // Workflow initial state is assigned server-side on create (presave →
        // first state of the type's workflow), so the form no longer sends it.
        createdAt: new Date().toISOString()
    },
    // 2e argument : l'INTENTION de la case « Créer un autre ticket ». Les
    // consommateurs qui referment la popin eux-mêmes (l'enveloppe
    // reactive-json des fiches CRM) doivent la connaître, sinon la popin se
    // ferme alors que le formulaire vient de se réinitialiser ici.
    { createAnother });

    if (createAnother) {
        setIsResetting(true);
        setTimeout(() => {
            setTitle('');
            setType(String(taskType || ''));
            setPriority(DEFAULT_PRIORITY);
            setComplexity(DEFAULT_COMPLEXITY);
            setTags([]);
            setReturnType('');
            setEvolutionType('');
            setDescription('');
            editor?.commands.setContent('');
            setAssignedTo('');
            setDueDate('');
            setFiles([]);  // files were attached to the just-created ticket; start fresh
            setIsResetting(false);
        }, 2000);
    } else {
        onClose();
    }
  };

  // `inline` is always rendered: it has no overlay to open or close, it simply
  // sits in the page flow. Only the modal form honours `isOpen`.
  // A type is ALWAYS preselected. The empty option below guards a controlled
  // <select> against displaying an option React does not know about; but with
  // nothing preselected, submitting does nothing at all — no message, no
  // highlight, since the type guard bails out silently. Preselecting keeps that
  // dead end out of reach, and the empty option is then only ever seen when a
  // project allows no type whatsoever.
  // `type` is a dependency too: opening the popin (and « Créer un autre ticket »)
  // resets it to `taskType`, empty when the caller imposes none — the CRM
  // records. Without it, the first type picked while the popin was still closed
  // was wiped on opening and never picked again.
  const clefTypes = visibleTypeOptions.map((o) => o.value).join(',');
  useEffect(() => {
    if (type || visibleTypeOptions.length === 0) return;
    setType(String(visibleTypeOptions[0].value));
  }, [clefTypes, type]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen && !inline) return null;

  // The CONTENT is identical in both mountings — header, form and footer are
  // written once. Only the shell differs: a fixed overlay with a floating card,
  // or a plain block that flows with the page. Splitting anything more would
  // duplicate a 200-line form for the sake of its frame.
  const card = (
      <div className={inline
        ? 'bg-white rounded-lg border border-slate-200 w-full flex flex-col overflow-hidden'
        : 'bg-white rounded-lg shadow-2xl w-[1000px] max-h-[90vh] flex flex-col overflow-hidden'}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          {/* Le TYPE remplace le titre de la popin : « Création de » + liste
              déroulante des types autorisés pour le projet ; si Evolution, le
              « Type d'évolution » se place juste à droite. */}
          <div className="flex items-center gap-3 flex-wrap min-w-0">
            <h2 className="sr-only">Création de {typeLabel || 'tâche'}</h2>
            {visibleTypeOptions.length > 0 ? (
              <>
                <label htmlFor="new-task-type" className="text-xl font-bold text-slate-800">Création de</label>
                <select
                  id="new-task-type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={`${selectBaseClass} w-56`}
                  required
                >
                  {/* EXPLICIT empty option while no type is picked. Without it a
                      React-side empty `type` matches no option, so the browser
                      displays the FIRST one: the user believes a type is chosen
                      that the component knows nothing about, then sees creation
                      refused without understanding why. Rendered only in that
                      case, so the Kanban list (type imposed) is unchanged. */}
                  {!type && <option value="">— Choisir un type —</option>}
                  {visibleTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </>
            ) : (
              <span className="text-xl font-bold text-slate-800" aria-hidden="true">Création de {typeLabel || 'tâche'}</span>
            )}
            {showEvolutionType && (
              <>
                <label htmlFor="new-task-evolution-type" className="text-xs font-bold text-slate-600 uppercase tracking-wide">Type d'évolution</label>
                <select
                  id="new-task-evolution-type"
                  value={evolutionType}
                  onChange={(e) => setEvolutionType(e.target.value)}
                  className={`${selectBaseClass} w-56 border-iris-200 bg-iris-50/30`}
                >
                  <option value="">Sélectionner...</option>
                  {evolutionTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          {/* Nothing to close when the form is part of the page. */}
          {!inline && (
          <button type="button" onClick={handleClose} aria-label="Fermer la fenêtre de création" className="text-slate-400 hover:text-slate-600">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-grow overflow-y-auto p-6 bg-slate-50 relative">
           {isResetting && (
             <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-lg">
               <svg className="animate-spin h-10 w-10 text-iris-600 mb-3" viewBox="0 0 24 24">
                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
               </svg>
               <span className="text-sm font-semibold text-iris-700">Ticket créé avec succès</span>
             </div>
           )}
           <form id="new-task-form" onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 space-y-6">
                
                {/* Row 1: Title & Tags — le Type est dans l'en-tête de la popin. */}
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-8">
                        <label className={labelClass}>Titre <span className="text-red-500">*</span></label>
                        <input 
                            type="text" 
                            required
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className={inputBaseClass}
                            placeholder="Titre du ticket..."
                        />
                    </div>
                    <div className="col-span-4">
                        <label className={labelClass}>Tags</label>
                        <div className="min-h-[46px] p-2 border-2 border-slate-300 rounded-lg flex flex-wrap gap-2 items-start bg-white hover:border-slate-400 transition-colors relative">
                            {tags.map((tagTid, idx) => (
                                <span key={idx} className="px-2.5 py-1 bg-iris-100 text-iris-700 rounded-md text-xs font-semibold flex items-center gap-1.5 border border-iris-200 shadow-sm">
                                    {findTagLabel(tagTid)}
                                    <button type="button" className="text-iris-400 hover:text-iris-800 focus:outline-none" onClick={() => setTags(tags.filter((_, i) => i !== idx))}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                    </button>
                                </span>
                            ))}
                            <div className="relative inline-block flex-grow">
                                <input
                                    type="text"
                                    placeholder={tags.length === 0 ? "Ajouter des tags..." : "+ Ajouter"}
                                    className="w-full text-sm border-none focus:ring-0 focus:outline-none p-1 [&::-webkit-calendar-picker-indicator]:hidden"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const val = e.target.value.trim();
                                            const tid = findTagTid(val);
                                            if (tid && !tags.includes(tid)) {
                                                setTags([...tags, tid]);
                                                e.target.value = '';
                                            }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const val = e.target.value.trim();
                                        const tid = findTagTid(val);
                                        if (tid && !tags.includes(tid)) {
                                            setTags([...tags, tid]);
                                            e.target.value = '';
                                        }
                                    }}
                                    list="new-task-available-tags"
                                />
                                <datalist id="new-task-available-tags">
                                    {availableTagNames.map(name => (
                                        <option key={name} value={name} />
                                    ))}
                                </datalist>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2 : Assigné à / Échéance / Priorité / Complexité — les attributs de la
                    tâche sur une seule ligne. Widgets priorité & complexité partagés
                    avec les cartes/la fiche. L'état initial du workflow est posé
                    côté serveur à la création (pas de champ de formulaire).
                    (Type de retour is hidden + optional now — see handleSubmit.
                    returnType state/setReturnType are kept so the (now-dead) isDemandeEvolution
                    lookup never crashes; the Evolution flow triggers via type === Evolution.) */}
                <div className="grid grid-cols-4 gap-6 items-start">
                    <div>
                        <label htmlFor="new-task-assignee" className={labelClass}>Assigné à</label>
                        <div className="border-2 border-slate-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-iris-500 focus-within:border-iris-500">
                            <UserSearchInput
                                id="new-task-assignee"
                                value={assignedTo}
                                onChange={(val) => setAssignedTo(val)}
                                placeholder="Rechercher..."
                                users={users}
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="new-task-due-date" className={labelClass}>Échéance</label>
                        <input
                            id="new-task-due-date"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className={inputBaseClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Priorité</label>
                        <div className="h-[42px] flex items-center">
                            <PriorityDropdown value={priority} onChange={setPriority} size={24} showLabel />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Complexité</label>
                        <div className="h-[42px] flex items-center">
                            <ComplexityDots value={complexity} onChange={setComplexity} size={16} />
                        </div>
                    </div>
                </div>

                {/* Description — TipTap rich-text editor */}
                <div>
                    <label className={labelClass}>Description</label>
                    <div className="border-2 border-slate-300 rounded-lg bg-white focus-within:border-iris-500 focus-within:ring-2 focus-within:ring-iris-500 transition-all overflow-hidden">
                        {editor && (
                            <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1">
                                {[
                                    { cmd: () => editor.chain().focus().toggleBold().run(), active: editor.isActive('bold'), label: 'B', cls: 'font-bold' },
                                    { cmd: () => editor.chain().focus().toggleItalic().run(), active: editor.isActive('italic'), label: 'I', cls: 'italic' },
                                    { cmd: () => editor.chain().focus().toggleBulletList().run(), active: editor.isActive('bulletList'), label: '•', cls: '' },
                                    { cmd: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive('orderedList'), label: '1.', cls: '' },
                                ].map((b, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); b.cmd(); }}
                                        className={`px-2 py-0.5 rounded text-sm hover:bg-slate-200 ${b.cls} ${b.active ? 'bg-iris-100 text-iris-700' : 'text-slate-600'}`}
                                    >
                                        {b.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <EditorContent editor={editor} />
                    </div>
                </div>

                {/* Files — deferred upload, reproduces the edit-page attachments. */}
                <div>
                    <label className={labelClass}>Fichiers</label>
                    <TaskFileUpload files={files} setFiles={setFiles} />
                </div>
           </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
            {/* Hidden in the report flow by DESIGN, not because it is broken:
                reporting is a one-shot gesture and the screen is kept as bare as
                possible. Chaining works there too — the save path honours
                `options.createAnother` — so dropping this guard is all it would
                take to offer it. */}
            <div className={`flex items-center ${inline ? 'invisible' : ''}`}>
                <input
                    id="create-another"
                    type="checkbox"
                    checked={createAnother}
                    onChange={(e) => setCreateAnother(e.target.checked)}
                    className="h-4 w-4 text-iris-600 focus:ring-iris-500 border-slate-300 rounded"
                />
                <label htmlFor="create-another" className="ml-2 block text-sm text-slate-900">
                    Créer un autre ticket
                </label>
            </div>
            <div className="flex gap-3 items-center">
                {/* Nothing to dismiss when the form is part of the page. */}
                {!inline && (
                  <button
                      type="button"
                      onClick={handleClose}
                      className="btn btn-secondary"
                  >
                      Annuler
                  </button>
                )}
                <button
                    type="submit"
                    form="new-task-form"
                    disabled={isResetting}
                    className={`btn btn-primary ${isResetting ? 'cursor-not-allowed' : ''}`}
                >
                    {createAnother ? 'Créer & Nouveau' : 'Créer'}
                </button>
            </div>
        </div>

      </div>
  );

  // Inline: straight into the page flow. Modal: the fixed, dimmed overlay.
  return inline ? card : (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-500/30 backdrop-blur-sm">
      {card}
    </div>
  );
};

export default NewTaskModal;
